import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { initializeApp, deleteApp } from "firebase/app";
import { db } from "../../firebase";
import app from "../../firebase";
import AdminShell from "../../components/AdminShell";
import { useAuth } from "../../contexts/AuthContext";

export default function AdminAccounts() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const snap = await getDocs(collection(db, "admins"));
      setAdmins(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const createAdmin = async () => {
    setMsg(""); setBusy(true);
    try {
      if (!name || !email || !password) { setMsg("All fields required"); setBusy(false); return; }
      if (password.length < 6) { setMsg("Password must be at least 6 characters"); setBusy(false); return; }
      // Use a secondary app instance so creating a user doesn't sign out the superadmin
      const secondaryApp = initializeApp(app.options, "admin-create-" + Date.now());
      const secondaryAuth = getAuth(secondaryApp);
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      await deleteApp(secondaryApp);
      await setDoc(doc(db, "admins", cred.user.uid), {
        role: "admin", name, email, createdAt: serverTimestamp()
      });
      setMsg(`Admin "${name}" created. They can now log in with ${email}.`);
      setName(""); setEmail(""); setPassword(""); setAdding(false);
      await load();
    } catch (e) { setMsg("Error: " + e.message); }
    setBusy(false);
  };

  const removeAdmin = async (admin) => {
    if (admin.role === "superadmin") { setMsg("Cannot delete a superadmin."); return; }
    if (!window.confirm(`Remove admin access for "${admin.name}"?`)) return;
    try {
      await deleteDoc(doc(db, "admins", admin.id));
      await load();
      setMsg("Admin removed.");
    } catch (e) { setMsg("Error: " + e.message); }
  };

  return (
    <AdminShell title="Manage Admin Accounts">
      <div className="alert alert-info">
        <i className="ti ti-info-circle"></i> Only Super Admins can manage admin accounts. Regular admins can manage applicants but cannot create or delete admin users.
      </div>
      {msg && <div className="alert alert-info">{msg}</div>}

      <div className="flex-between mb-16">
        <p className="text-muted">Total admins: {admins.length}</p>
        {!adding && <button className="btn btn-primary" onClick={() => setAdding(true)}><i className="ti ti-user-plus"></i> Add New Admin</button>}
      </div>

      {adding && (
        <div className="card mb-24">
          <h3 style={{ fontSize: 18, marginBottom: 16 }}>Create New Admin</h3>
          <div className="form-row cols-2">
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Initial Password (min 6 chars)</label>
            <input className="form-control" type="text" value={password} onChange={(e) => setPassword(e.target.value)} />
            <small className="text-muted">Share securely with the new admin. They can change it later.</small>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" onClick={createAdmin} disabled={busy}>
              {busy ? <span className="spinner"></span> : <><i className="ti ti-check"></i> Create Admin</>}
            </button>
            <button className="btn btn-outline" onClick={() => { setAdding(false); setName(""); setEmail(""); setPassword(""); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        {admins.length === 0 ? (
          <div className="empty-state"><i className="ti ti-user-off"></i><div>No admins found</div></div>
        ) : (
          <div className="table-wrap"><table className="table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th></th></tr></thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id}>
                  <td><strong>{a.name || "â€”"}</strong></td>
                  <td>{a.email}</td>
                  <td>
                    <span className={`badge ${a.role === "superadmin" ? "badge-ready" : "badge-accepted"}`}>
                      {a.role}
                    </span>
                  </td>
                  <td>
                    {a.role !== "superadmin" && a.id !== user?.uid && (
                      <button className="btn btn-danger" onClick={() => removeAdmin(a)}>
                        <i className="ti ti-trash"></i> Remove
                      </button>
                    )}
                    {a.id === user?.uid && <span className="text-muted" style={{ fontSize: 12 }}>(you)</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </AdminShell>
  );
}

